// tippi/src/pages/admin/masses/AdminMasses.js
import React, { useState, useEffect, useRef } from 'react';
import { COLORS, SHADOWS } from '../../../styles/tokens';
import { adminApi } from '../../../firebase/functionsService';
import { useMassCatalog, validateCatalogDraft, activeOptions } from '../../../utils/massCatalog';
import {
  addType, addOption, updateType, updateOption, moveItem, setTypes, setOptions,
} from '../../../utils/massCatalogEdit';
import { EQUIPMENT_OPTIONS } from '../../../utils/loadingEquipment';

const ICON_OPTIONS = [
  { value: 'layers', label: 'Lag' },
  { value: 'pile', label: 'Haug' },
  { value: 'plus', label: 'Pluss' },
  { value: 'truck', label: 'Lastebil' },
];

const REGISTER_COLS = '20px 1fr 110px 100px 70px';

const sortByOrder = (items) => [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

export default function AdminMasses() {
  const stored = useMassCatalog();
  // Utkastet startes fra den lagrede katalogen og redigeres lokalt til admin trykker Lagre
  const [draft, setDraft] = useState(() => stored);
  const [baseVersion, setBaseVersion] = useState(() => stored.version ?? 0);
  const [newType, setNewType] = useState('');
  const [newOptions, setNewOptions] = useState({});
  const [errors, setErrors] = useState([]);
  const [status, setStatus] = useState(null); // null | 'saving' | 'ok'
  // Registeret er sammenslått som standard; admin utvider raden som skal redigeres
  const [open, setOpen] = useState(() => new Set());
  function toggleOpen(key) {
    setOpen(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  // Så lenge admin ikke har redigert, følger utkastet den lagrede katalogen (første Firestore-snapshot kan komme etter visning)
  const dirty = useRef(false);
  useEffect(() => {
    if (dirty.current) return;
    setDraft(stored);
    setBaseVersion(stored.version ?? 0);
  }, [stored]);

  const types = sortByOrder(draft.types);

  function edit(fn) { dirty.current = true; setDraft(fn); setStatus(null); }

  function patchType(key, patch) { edit(d => updateType(d, key, patch)); }
  function patchOption(typeKey, optionKey, patch) { edit(d => updateOption(d, typeKey, optionKey, patch)); }

  function moveType(index, dir) {
    edit(d => setTypes(d, moveItem(d.types, index, dir)));
  }
  function moveOption(type, index, dir) {
    edit(d => setOptions(d, type.key, moveItem(d.types.find(t => t.key === type.key).options, index, dir)));
  }

  function toggleEquipment(type, value) {
    const has = type.defaultEquipment.includes(value);
    patchType(type.key, { defaultEquipment: has ? type.defaultEquipment.filter(v => v !== value) : [...type.defaultEquipment, value] });
  }

  function handleAddType() {
    if (!newType.trim()) return;
    const next = addType(draft, newType);
    const known = new Set(draft.types.map(t => t.key));
    const added = next.types.find(t => !known.has(t.key));
    edit(() => next);
    if (added) setOpen(prev => new Set(prev).add(added.key)); // ny type åpnes så den kan fylles ut
    setNewType('');
  }

  function handleAddOption(typeKey) {
    const label = (newOptions[typeKey] ?? '').trim();
    if (!label) return;
    edit(d => addOption(d, typeKey, label));
    setNewOptions(o => ({ ...o, [typeKey]: '' }));
  }

  async function handleSave() {
    const problems = validateCatalogDraft(draft);
    if (problems.length > 0) { setErrors(problems); setStatus(null); return; }
    setErrors([]);
    setStatus('saving');
    try {
      const res = await adminApi.saveMassCatalog(draft, baseVersion);
      setBaseVersion(res.version);
      setDraft(d => ({ ...d, version: res.version }));
      dirty.current = false;
      setStatus('ok');
    } catch (err) {
      setStatus(null);
      setErrors([err?.message || 'Kunne ikke lagre']);
    }
  }

  return (
    <div style={styles.page}>
      <h1 style={styles.h1}>Masser</h1>
      <p style={styles.lead}>
        Massetyper og undervalg som bestillere og sjåfører ser. Typer og valg kan skrus av, men ikke slettes, så eldre oppdrag fortsatt vises riktig.
      </p>
      {baseVersion === 0 && (
        <p style={styles.note}>Katalogen bruker standardverdier til du lagrer første gang.</p>
      )}

      <fieldset disabled={status === 'saving'} style={styles.editing}>
      <div style={styles.registerHead} aria-hidden="true">
        <span /><span>Massetype</span><span>Undervalg</span><span>Prisfaktor</span><span>Status</span>
      </div>
      <div style={styles.register}>
      {types.map((type, index) => {
        const isOpen = open.has(type.key);
        const optionCount = activeOptions(type).length;
        return (
        <div key={type.key} style={{ ...styles.regItem, opacity: type.active ? 1 : 0.6 }}>
          <button type="button" aria-expanded={isOpen} onClick={() => toggleOpen(type.key)} style={styles.regRow}>
            <span style={styles.chev} aria-hidden="true">{isOpen ? '▾' : '▸'}</span>
            <span style={styles.regName}>{type.label || type.key}</span>
            <span style={styles.regCell}>{optionCount > 0 ? `${optionCount} undervalg` : '—'}</span>
            <span style={styles.regCell}>× {type.priceFactor}</span>
            <span style={type.active ? styles.badgeOn : styles.badgeOff}>{type.active ? 'Aktiv' : 'Av'}</span>
          </button>
          {isOpen && (
        <section role="group" aria-label={type.label || type.key} style={styles.regBody}>
          <div style={styles.row}>
            <input aria-label="Navn" value={type.label}
                   onChange={e => patchType(type.key, { label: e.target.value })} style={{ ...styles.input, flex: 1 }} />
            <label style={styles.check}>
              <input type="checkbox" aria-label="Aktiv" checked={type.active}
                     onChange={e => patchType(type.key, { active: e.target.checked })} /> Aktiv
            </label>
            <button type="button" aria-label="Flytt opp" disabled={index === 0} onClick={() => moveType(index, -1)} style={styles.iconBtn}>↑</button>
            <button type="button" aria-label="Flytt ned" disabled={index === types.length - 1} onClick={() => moveType(index, 1)} style={styles.iconBtn}>↓</button>
          </div>
          <div style={styles.key}>Nøkkel: {type.key}</div>

          <div style={styles.grid}>
            <label style={styles.field}>Prisfaktor
              <input aria-label="Prisfaktor" type="number" step="0.05" min="0.5" max="3" value={type.priceFactor}
                     onChange={e => patchType(type.key, { priceFactor: Number(e.target.value) })} style={styles.input} />
            </label>
            <label style={styles.field}>Ikon
              <select aria-label="Ikon" value={type.icon} onChange={e => patchType(type.key, { icon: e.target.value })} style={styles.input}>
                {ICON_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <label style={styles.check}>
              <input type="checkbox" aria-label="Kan leveres i sekk" checked={type.supportsBags}
                     onChange={e => patchType(type.key, { supportsBags: e.target.checked })} /> Kan leveres i sekk
            </label>
          </div>

          <fieldset style={styles.fieldset}>
            <legend style={styles.legend}>Forslag til lasteutstyr</legend>
            {EQUIPMENT_OPTIONS.map(o => (
              <label key={o.value} style={styles.check}>
                <input type="checkbox" aria-label={`Utstyr ${o.label}`} checked={type.defaultEquipment.includes(o.value)}
                       onChange={() => toggleEquipment(type, o.value)} /> {o.label}
              </label>
            ))}
          </fieldset>

          <div style={styles.grid}>
            <label style={styles.field}>Etikett for undervalg
              <input aria-label="Etikett for undervalg" value={type.subLabel} placeholder="f.eks. Kornstørrelse"
                     onChange={e => patchType(type.key, { subLabel: e.target.value })} style={styles.input} />
            </label>
            <label style={styles.check}>
              <input type="checkbox" aria-label="Undervalg påkrevd" checked={type.subRequired}
                     onChange={e => patchType(type.key, { subRequired: e.target.checked })} /> Undervalg påkrevd
            </label>
          </div>

          <label style={{ ...styles.field, marginBottom: 8 }}>Hjelpetekst (bruksområde)
            <input aria-label="Hjelpetekst for massetype" value={type.hint ?? ''} maxLength={120}
                   placeholder="f.eks. Bærelag under veier og plasser"
                   onChange={e => patchType(type.key, { hint: e.target.value })} style={styles.input} />
          </label>

          <ul style={styles.list}>
            {sortByOrder(type.options).map((o, j) => (
              <li key={o.key} style={{ marginBottom: 8, opacity: o.active ? 1 : 0.6 }}>
                <div style={styles.row}>
                  <input aria-label={`Navn på undervalg ${o.key}`} value={o.label}
                         onChange={e => patchOption(type.key, o.key, { label: e.target.value })} style={{ ...styles.input, flex: 1 }} />
                  <label style={styles.check}>
                    <input type="checkbox" aria-label={`Aktivt undervalg ${o.key}`} checked={o.active}
                           onChange={e => patchOption(type.key, o.key, { active: e.target.checked })} /> Aktiv
                  </label>
                  <button type="button" aria-label={`Flytt undervalg ${o.key} opp`} disabled={j === 0}
                          onClick={() => moveOption(type, j, -1)} style={styles.iconBtn}>↑</button>
                  <button type="button" aria-label={`Flytt undervalg ${o.key} ned`} disabled={j === type.options.length - 1}
                          onClick={() => moveOption(type, j, 1)} style={styles.iconBtn}>↓</button>
                </div>
                <input aria-label={`Hjelpetekst for undervalg ${o.key}`} value={o.hint ?? ''} maxLength={120}
                       placeholder="Hjelpetekst (bruksområde)"
                       onChange={e => patchOption(type.key, o.key, { hint: e.target.value })}
                       style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }} />
              </li>
            ))}
          </ul>
          <div style={styles.row}>
            <input aria-label="Nytt undervalg" value={newOptions[type.key] ?? ''} placeholder="Nytt undervalg"
                   onChange={e => setNewOptions(o => ({ ...o, [type.key]: e.target.value }))} style={{ ...styles.input, flex: 1 }} />
            <button type="button" onClick={() => handleAddOption(type.key)} style={styles.secondaryBtn}>Legg til undervalg</button>
          </div>
        </section>
          )}
        </div>
        );
      })}
      </div>

      <div style={styles.card}>
        <div style={styles.row}>
          <input aria-label="Ny massetype" value={newType} placeholder="Ny massetype, f.eks. Bark"
                 onChange={e => setNewType(e.target.value)} style={{ ...styles.input, flex: 1 }} />
          <button type="button" onClick={handleAddType} style={styles.secondaryBtn}>Legg til massetype</button>
        </div>
      </div>
      </fieldset>

      {errors.length > 0 && (
        <div role="alert" style={styles.error}>
          {errors.map(e => <div key={e}>{e}</div>)}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
        {status === 'ok' && <span style={{ color: COLORS.success, fontSize: 13 }}>✓ Lagret</span>}
        <button type="button" onClick={handleSave} disabled={status === 'saving'} style={styles.primaryBtn}>Lagre</button>
      </div>
    </div>
  );
}

const styles = {
  editing: { border: 0, padding: 0, margin: 0, minWidth: 0 },
  page: { padding: 24, maxWidth: 820, margin: '0 auto', overflowY: 'auto' },
  h1: { margin: '0 0 8px', fontSize: 22, color: COLORS.textDark },
  lead: { margin: '0 0 16px', fontSize: 14, color: COLORS.textLight },
  note: { margin: '0 0 16px', fontSize: 13, color: COLORS.textDark, background: COLORS.surfaceHover, padding: '8px 12px', borderRadius: 8 },
  registerHead: { display: 'grid', gridTemplateColumns: REGISTER_COLS, gap: 8, padding: '0 14px 6px', fontSize: 11, fontWeight: 700, color: COLORS.textLight, textTransform: 'uppercase', letterSpacing: 0.6 },
  register: { background: COLORS.white, borderRadius: 12, boxShadow: SHADOWS.sm, marginBottom: 16, overflow: 'hidden' },
  regItem: { borderBottom: `1px solid ${COLORS.border}` },
  regRow: { display: 'grid', gridTemplateColumns: REGISTER_COLS, gap: 8, alignItems: 'center', width: '100%', padding: '12px 14px', border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 14, color: COLORS.textDark },
  regName: { fontWeight: 700 },
  regCell: { fontSize: 13, color: COLORS.textMid },
  chev: { color: COLORS.textLight },
  badgeOn: { justifySelf: 'start', fontSize: 11, fontWeight: 700, color: COLORS.successText, background: COLORS.successBg, borderRadius: 99, padding: '2px 10px' },
  badgeOff: { justifySelf: 'start', fontSize: 11, fontWeight: 700, color: COLORS.textLight, background: COLORS.surfaceHover, borderRadius: 99, padding: '2px 10px' },
  regBody: { padding: '4px 16px 16px', background: COLORS.surfaceHover },
  card: { background: COLORS.white, borderRadius: 12, boxShadow: SHADOWS.sm, padding: 16, marginBottom: 16 },
  row: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  grid: { display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginBottom: 8 },
  field: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: COLORS.textDark },
  input: { padding: '8px 10px', borderRadius: 7, border: `1.5px solid ${COLORS.border}`, fontSize: 14, background: COLORS.white, color: COLORS.textDark },
  check: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: COLORS.textDark },
  key: { fontSize: 11, color: COLORS.textLight, marginBottom: 8 },
  fieldset: { border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: '4px 12px 8px', marginBottom: 8, display: 'flex', flexWrap: 'wrap', gap: 12 },
  legend: { fontSize: 12, color: COLORS.textLight, padding: '0 4px' },
  list: { listStyle: 'none', margin: '0 0 8px', padding: 0 },
  iconBtn: { width: 32, height: 32, borderRadius: 8, border: `1px solid ${COLORS.border}`, background: COLORS.white, cursor: 'pointer' },
  secondaryBtn: { padding: '8px 14px', borderRadius: 8, border: `1px solid ${COLORS.border}`, background: COLORS.white, color: COLORS.textDark, fontWeight: 600, cursor: 'pointer' },
  primaryBtn: { padding: '9px 22px', borderRadius: 8, border: 'none', background: COLORS.primary, color: COLORS.onPrimary, fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  error: { color: COLORS.danger, fontSize: 13, margin: '0 0 12px' },
};
